import { ReactNode } from 'react';

import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';

type ConfirmActionModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel: string;
  /** Suffixed with `_cancel` and `_confirm` for the two buttons. */
  analyticsIdPrefix: string;
  /** Keeps the modal open and both buttons inert while the action is in flight. */
  isLoading?: boolean;
  children?: ReactNode;
};

export const ConfirmActionModal = ({
  open,
  setOpen,
  onConfirm,
  title,
  description,
  confirmLabel,
  analyticsIdPrefix,
  isLoading = false,
  children,
}: ConfirmActionModalProps) => {
  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setOpen(false);
  };

  return (
    <BasePopup open={open} setOpen={handleClose}>
      <BasePopup.Body>
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
        <div className="mt-2 flex flex-col gap-6">
          <p className="text-sm text-gray-500">{description}</p>
          {children}
          <div className="flex items-center justify-end gap-2">
            <Button
              analyticsId={`${analyticsIdPrefix}_cancel`}
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              <Translate i18nKey="common:cancel" />
            </Button>
            <Button
              analyticsId={`${analyticsIdPrefix}_confirm`}
              variant="primary"
              onClick={onConfirm}
              loading={isLoading}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};
